import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    Modal, Button, Table, Spinner, Badge, Form, Alert, Row, Col, InputGroup, FormControl, 
    ListGroup, Dropdown, Breadcrumb, ProgressBar
} from 'react-bootstrap';
import {
    FaFolder, FaFileAlt, FaCloudUploadAlt, FaTrash, FaDownload, FaCog, FaEdit, 
    FaFolderPlus, FaSyncAlt, FaSearch, FaEllipsisV, FaUserCircle, FaBuilding
} from 'react-icons/fa';
import { cloudAPI } from '../../utils/httpClient';
import styled from 'styled-components';
import Select from 'react-select';
import VectorizationCombinationManager from './VectorizationCombinationManager';
import SpreadsheetEditorModal from './SpreadsheetEditorModal';
import { useI18n } from '../../i18n';

// --- Styled Components ---
const CloudSpaceContainer = styled(Modal.Body)`
    display: flex;
    flex-direction: column;
    height: 70vh;
    overflow: hidden;
`;

const HeaderSection = styled.div`
    padding: 10px 15px;
    border-bottom: 1px solid #e9ecef;
    display: flex;
    justify-content: space-between;
    align-items: center;
`;

const ActionButtons = styled.div`
    display: flex;
    gap: 10px;
`;

const MainContent = styled.div`
    flex-grow: 1;
    display: flex;
    overflow: hidden;
`;

const Sidebar = styled.div`
    width: 200px;
    padding: 15px 0;
    border-right: 1px solid #e9ecef;
    background-color: #f8f9fa;
    flex-shrink: 0;
`;

const FileBrowser = styled.div`
    flex-grow: 1;
    padding: 15px;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    width: 100%;
`;

const ItemList = styled.div`
    flex-grow: 1;
    overflow-y: auto;
    border: 1px solid #e9ecef;
    border-radius: 0.25rem;
    width: 100%;
    
    .table-responsive {
        width: 100%;
        overflow-x: visible !important;
    }
    
    table.table {
        width: 100% !important;
        margin-bottom: 0;
    }
`;

const ItemRow = styled.tr`
    cursor: pointer;
    transition: background-color 0.15s ease, box-shadow 0.15s ease;
    &:hover {
        background-color: #f1f3f5;
    }
    &.selected {
        background-color: #e7f5ff !important;
        box-shadow: inset 4px 0 0 #0d6efd;
        font-weight: 600;
    }
    &.selected td:first-child {
        border-left: 4px solid #0d6efd;
    }
`;

const ItemIcon = styled.span`
    margin-right: 10px;
    color: #495057;
`;

const FooterSection = styled(Modal.Footer)`
    border-top: 1px solid #e9ecef;
`;

const FileName = styled.span`
    user-select: none;
`;

const UploadProgress = styled.div`
    position: absolute;
    top: 10px;
    right: 10px;
    background: white;
    border: 1px solid #ddd;
    border-radius: 8px;
    padding: 15px;
    box-shadow: 0 4px 12px rgba(0,0,0,0.15);
    min-width: 300px;
    z-index: 1040;
`;

// --- Helper Functions ---
const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString();
};

const formatBytes = (bytes) => {
    if (!bytes) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

// Authentication helper function
const getAuthHeaders = () => {
    try {
        const userInfoStr = localStorage.getItem('userInfo');
        if (!userInfoStr) {
            console.error("userInfo not found in localStorage");
            return null;
        }

        const userInfo = JSON.parse(userInfoStr);
        const userId = userInfo?.id || userInfo?.user_id;
        const orgCode = userInfo?.org_code;
        const userLevel = userInfo?.user_level;

        if (!userId) {
            console.error("User ID not found in parsed userInfo");
            return null;
        }

        const headers = {
            'X-User-Id': userId.toString(),
        };
        if (orgCode) {
            headers['X-Org-Code'] = orgCode;
        }
        if (userLevel !== undefined && userLevel !== null) {
            headers['X-User-Level'] = userLevel.toString();
        }
        return headers;
    } catch (error) {
        console.error("Error parsing userInfo from localStorage:", error);
        return null;
    }
};

// --- API Service ---
const apiService = {
    getItems: async (parentId = null, includeOrgItems = true, onlyOrg = false) => {
        return await cloudAPI.listItems(parentId, includeOrgItems, onlyOrg);
    },
    createFolder: async (name, parentId, forOrg) => {
        return await cloudAPI.createFolder(name, parentId, forOrg);
    },
    uploadFile: async (file, parentId, forOrg, onProgress) => {
        return await cloudAPI.uploadFile(file, parentId, forOrg, onProgress);
    },
    renameItem: async (itemId, newName) => {
        return await cloudAPI.renameItem(itemId, newName);
    },
    deleteItem: async (itemId) => {
        return await cloudAPI.deleteItem(itemId);
    },
    vectorizeFile: async (itemId, sourceColumn, targetColumns) => {
        return await cloudAPI.startVectorization(itemId, sourceColumn, targetColumns);
    },
    downloadFile: async (itemId, fileName) => {
        const response = await cloudAPI.downloadFile(itemId);
        const blob = new Blob([response.data], { type: response.headers['content-type'] || 'application/octet-stream' });
        const downloadUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
        link.parentNode.removeChild(link);
        window.URL.revokeObjectURL(downloadUrl);
    },
    getVectorizationProgress: async (taskId) => {
        return await cloudAPI.getVectorizationProgress(taskId);
    },
    getActiveVectorizations: async () => {
        return await cloudAPI.getActiveVectorizations();
    },
    clearVectorizationStatus: async (itemId) => {
        return await cloudAPI.clearVectorizationStatus(itemId);
    }
};

// --- Modals ---
const VectorizeConfigModal = ({ show, onHide, file, onConfirm, loading }) => {
    const [sourceCol, setSourceCol] = useState('');
    const [targetCols, setTargetCols] = useState([]);
    const [modalError, setModalError] = useState(null);
    const { t } = useI18n();

    useEffect(() => {
        if (file && file.columns && file.columns.length > 0) {
            setSourceCol(file.columns[0]);
            setTargetCols([]);
            setModalError(null);
        } else if (file) {
            setSourceCol('');
            setTargetCols([]);
            setModalError(t('cloud:cloudSpace.errors.noColumns', { name: file.name }));
        }
    }, [file, show]);

    const handleConfirm = () => {
        if (!sourceCol) {
            setModalError(t('cloud:cloudSpace.errors.selectSource'));
            return;
        }
        const finalTargetCols = targetCols.filter(col => col !== sourceCol);
        onConfirm(file.id, sourceCol, finalTargetCols);
    };

    const columnOptions = file?.columns?.map(col => ({ value: col, label: col })) || [];
    const targetColumnOptions = columnOptions.filter(opt => opt.value !== sourceCol);

    return (
        <Modal show={show} onHide={onHide}>
            <Modal.Header closeButton>
                <Modal.Title>{t('cloud:cloudSpace.titleVectorize', { name: file?.name })}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {modalError && <Alert variant="danger">{modalError}</Alert>}
                <Form.Group className="mb-3">
                    <Form.Label><strong>{t('cloud:cloudSpace.labels.sourceColumn')}</strong></Form.Label>
                    <Form.Select 
                        value={sourceCol}
                        onChange={(e) => {
                            setSourceCol(e.target.value);
                            setTargetCols(prev => prev.filter(col => col !== e.target.value)); 
                        }}
                        disabled={!file || !file.columns || file.columns.length === 0}
                    >
                        <option value="" disabled>{t('cloud:combination.sourceSelectPlaceholder')}</option>
                        {columnOptions.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                    </Form.Select>
                    <Form.Text className="text-muted">{t('cloud:cloudSpace.labels.sourceHint')}</Form.Text>
                </Form.Group>
                <Form.Group className="mb-3">
                    <Form.Label><strong>{t('cloud:cloudSpace.labels.targetColumns')}</strong></Form.Label>
                    <Select
                        isMulti
                        options={targetColumnOptions}
                        value={targetCols.map(col => ({ value: col, label: col }))}
                        onChange={(selected) => {
                            setTargetCols(selected ? selected.map(item => item.value) : []);
                        }}
                        isDisabled={!sourceCol || targetColumnOptions.length === 0}
                        placeholder={t('cloud:cloudSpace.labels.targetColumns')}
                    />
                    <Form.Text className="text-muted">{t('cloud:cloudSpace.labels.targetHint')}</Form.Text>
                </Form.Group>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onHide} disabled={loading}>{t('common:actions.cancel')}</Button>
                <Button variant="primary" onClick={handleConfirm} disabled={loading || !sourceCol}>
                    {loading ? <><Spinner size="sm" /> {t('cloud:cloudSpace.labels.processing')}</> : t('cloud:cloudSpace.labels.confirmVectorize')}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

const RenameModal = ({ show, onHide, currentName, onConfirm, loading }) => {
    const [newName, setNewName] = useState('');
    const { t } = useI18n();

    useEffect(() => {
        if (show && currentName) {
            setNewName(currentName);
        }
    }, [show, currentName]);

    const handleSubmit = (e) => {
        e.preventDefault();
        if (newName.trim() && newName !== currentName) {
            onConfirm(newName.trim());
        }
    };

    return (
        <Modal show={show} onHide={onHide} centered size="sm">
            <Modal.Header closeButton>
                <Modal.Title>{t('cloud:cloudSpace.labels.renameItem')}</Modal.Title>
            </Modal.Header>
            <Form onSubmit={handleSubmit}>
                <Modal.Body>
                    <Form.Group>
                        <Form.Label>{t('cloud:cloudSpace.labels.newName')}</Form.Label>
                        <Form.Control
                            type="text"
                            value={newName}
                            onChange={(e) => setNewName(e.target.value)}
                            autoFocus
                            disabled={loading}
                        />
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={onHide} disabled={loading}>{t('common:actions.cancel')}</Button>
                    <Button variant="primary" type="submit" disabled={loading || !newName.trim() || newName === currentName}>
                        {loading ? <><Spinner size="sm" /> {t('cloud:cloudSpace.labels.renaming')}</> : t('cloud:cloudSpace.labels.rename')}
                    </Button>
                </Modal.Footer>
            </Form>
        </Modal>
    );
};

const DeleteConfirmationModal = ({ show, onHide, itemName, onConfirm, loading }) => {
    const { t } = useI18n();
    return (
    <Modal show={show} onHide={onHide} centered size="sm">
        <Modal.Header closeButton>
            <Modal.Title>{t('cloud:cloudSpace.labels.confirmDeletion')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
            <p>{t('cloud:cloudSpace.labels.deleteConfirmLine', { name: itemName })}</p>
            <p className="text-danger"><small>{t('cloud:cloudSpace.labels.deleteWarning')}</small></p>
        </Modal.Body>
        <Modal.Footer>
            <Button variant="secondary" onClick={onHide} disabled={loading}>{t('common:actions.cancel')}</Button>
            <Button variant="danger" onClick={onConfirm} disabled={loading}>
                {loading ? <><Spinner size="sm" /> {t('cloud:cloudSpace.labels.deleting')}</> : t('common:actions.delete', { defaultValue: 'Delete' })}
            </Button>
        </Modal.Footer>
    </Modal>
)};

// --- Main Component ---
const CloudSpaceNew = ({ show, onHide, onSelectFile, mode }) => {
    const { t } = useI18n();
    // Core state
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [selectedItemId, setSelectedItemId] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    
    // Navigation state
    const [currentFolder, setCurrentFolder] = useState(null);
    const [breadcrumbs, setBreadcrumbs] = useState([]);
    const [currentScope, setCurrentScope] = useState('user');
    
    // Upload state
    const [uploadProgress, setUploadProgress] = useState(null);
    
    // Action loading state
    const [actionLoading, setActionLoading] = useState(false);
    
    // Modal states
    const [showNewFolderModal, setShowNewFolderModal] = useState(false);
    const [showVectorizeModal, setShowVectorizeModal] = useState(false);
    const [showCombinationManager, setShowCombinationManager] = useState(false);
    const [showRenameModal, setShowRenameModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showSpreadsheetEditor, setShowSpreadsheetEditor] = useState(false);
    const [editingSpreadsheetItem, setEditingSpreadsheetItem] = useState(null);
    
    // Modal data states
    const [itemToVectorize, setItemToVectorize] = useState(null);
    const [itemToManage, setItemToManage] = useState(null);
    const [itemToRename, setItemToRename] = useState(null);
    const [itemToDelete, setItemToDelete] = useState(null);
    
    // Vectorization tracking
    const [vectorizationTasks, setVectorizationTasks] = useState({});
    const intervalRefs = useRef({});
    
    // File input ref
    const fileInputRef = useRef(null);
    
    // User info
    const [userInfo, setUserInfo] = useState(null);

    // Get user info
    const getUserInfo = useCallback(() => {
        try {
            const userInfoStr = localStorage.getItem('userInfo');
            if (!userInfoStr) return null;
            
            const parsed = JSON.parse(userInfoStr);
            return {
                id: parsed?.id || parsed?.user_id,
                orgCode: parsed?.org_code,
                level: parseInt(parsed?.user_level || '1', 10)
            };
        } catch (error) {
            console.error("Error parsing userInfo:", error);
            return null;
        }
    }, []);

    // Load items
    const loadItems = useCallback(async (parentId = null, scope = 'user') => {
        setLoading(true);
        setError(null);
        
        try {
            // user scope: only personal; org scope: only organization
            const includeOrgItems = false;
            const onlyOrg = scope === 'org';
            const response = await apiService.getItems(parentId, includeOrgItems, onlyOrg);
            
            if (response.success) {
                setItems(response.data.items || []);
                setCurrentFolder(response.data.current_folder);
                setBreadcrumbs(response.data.breadcrumbs || []);
                setCurrentScope(scope);
                // Clear any stale per-item task entries for files already vectorized
                if (response.data.items && response.data.items.length > 0) {
                    setVectorizationTasks(prev => {
                        const updated = { ...prev };
                        try {
                            for (const it of response.data.items) {
                                if (it && it.item_type === 'file' && it.vectorized && updated[it.id]) {
                                    delete updated[it.id];
                                }
                            }
                        } catch (_) { /* noop */ }
                        return updated;
                    });
                }
                
                // Debug logging for folder navigation
                console.log('loadItems result:', {
                    parentId: parentId,
                    scope: scope,
                    current_folder: response.data.current_folder,
                    breadcrumbs: response.data.breadcrumbs,
                    items_count: response.data.items?.length || 0
                });
            } else {
                setError({ message: response.message || t('cloud:cloudSpace.errors.failedLoadItems') });
            }
        } catch (err) {
            console.error('Error loading items:', err);
            setError({ 
                message: t('cloud:cloudSpace.errors.failedLoadItems') 
            });
        } finally {
            setLoading(false);
        }
    }, []);

    // Initialize component
    useEffect(() => {
        if (show) {
            const user = getUserInfo();
            setUserInfo(user);
            loadItems(null, 'user');
            restoreActiveVectorizations();
        }
        
        return () => {
            // Cleanup intervals on unmount
            Object.values(intervalRefs.current).forEach(clearInterval);
            intervalRefs.current = {};
        };
    }, [show, loadItems, getUserInfo]);

    // Restore active vectorizations
    const restoreActiveVectorizations = async () => {
        try {
            const response = await apiService.getActiveVectorizations();
            if (response.success && response.active_vectorizations) {
                const activeTasks = response.active_vectorizations;
                
                if (Object.keys(activeTasks).length > 0) {
                    const frontendTasks = {};
                    
                    // Validate each task by checking its actual status
                    for (const [itemId, taskData] of Object.entries(activeTasks)) {
                        try {
                            // Check if the task is actually still running
                            const progressData = await apiService.getVectorizationProgress(taskData.task_id);
                            
                            if (progressData.success) {
                                const { state } = progressData.data;
                                
                                // Only restore tasks that are actually still running
                                if (state === 'PENDING' || state === 'PROGRESS') {
                                    frontendTasks[itemId] = {
                                        taskId: taskData.task_id,
                                        status: state === 'PROGRESS' ? 'PROGRESS' : 'PENDING',
                                        progress: taskData.progress || 0,
                                        description: taskData.description || 'Restoring task status',
                                        error: null
                                    };
                                    
                                    // Start polling for this task
                                    startProgressPolling(itemId, taskData.task_id);
                                } else {
                                    // Task is completed or failed, clear it from backend
                                    try {
                                        await apiService.clearVectorizationStatus(itemId);
                                    } catch (e) {
                                        console.warn(`Failed to clear stale task for item ${itemId}:`, e);
                                    }
                                }
                            } else {
                                // Task status unknown, clear it
                                try {
                                    await apiService.clearVectorizationStatus(itemId);
                                } catch (e) {
                                    console.warn(`Failed to clear unknown task for item ${itemId}:`, e);
                                }
                            }
                        } catch (error) {
                            console.warn(`Failed to validate task ${taskData.task_id} for item ${itemId}:`, error);
                            // Clear invalid task
                            try {
                                await apiService.clearVectorizationStatus(itemId);
                            } catch (e) {
                                console.warn(`Failed to clear invalid task for item ${itemId}:`, e);
                            }
                        }
                    }
                    
                    setVectorizationTasks(frontendTasks);
                }
            }
        } catch (error) {
            console.error('Failed to restore active vectorization tasks:', error);
        }
    };

    // Permission checking
    const canModify = (item) => {
        if (!userInfo) return false;
        
        if (item.org_code) {
            return userInfo.orgCode === item.org_code && userInfo.level >= 1;
        } else {
            return userInfo.id === item.user_id;
        }
    };

    // Event handlers
    const handleItemClick = (item) => {
        setSelectedItemId(item.id);
    };

    const handleItemDoubleClick = (item) => {
        if (item.item_type === 'folder') {
            loadItems(item.id, item.org_code ? 'org' : 'user');
        } else if (item.item_type === 'file') {
            // Check if it's a spreadsheet file
            const fileType = (item.file_type || '').toLowerCase().replace('.', '');
            const fileName = (item.name || '').toLowerCase();
            const isSpreadsheet = ['csv', 'xlsx', 'xls'].includes(fileType) ||
                fileName.endsWith('.csv') || fileName.endsWith('.xlsx') || fileName.endsWith('.xls');

            if (isSpreadsheet) {
                setEditingSpreadsheetItem(item);
                setShowSpreadsheetEditor(true);
            }
        }
    };

    const handleBreadcrumbClick = (crumb) => {
        loadItems(crumb.id, crumb.scope || 'user');
    };

    // File upload
    const handleFileUpload = async (event) => {
        const files = Array.from(event.target.files);
        if (files.length === 0) return;

        // Clear file input
        event.target.value = '';

        const parentId = currentFolder?.id || null;
        const forOrg = currentScope === 'org';
        
        // Debug logging for upload path
        console.log('File upload debug:', {
            currentFolder: currentFolder,
            parentId: parentId,
            currentScope: currentScope,
            breadcrumbs: breadcrumbs
        });

        for (const file of files) {
            try {
                setUploadProgress({ fileName: file.name, progress: 0 });
                
                const response = await apiService.uploadFile(
                    file, 
                    parentId, 
                    forOrg,
                    (progress) => {
                        setUploadProgress({ fileName: file.name, progress });
                    }
                );
                
                if (response.success) {
                    setUploadProgress({ fileName: file.name, progress: 100, completed: true });
                    setTimeout(() => {
                        loadItems(parentId, currentScope);
                        setUploadProgress(null);
                    }, 1000);
                } else {
                    throw new Error(response.message || t('cloud:cloudSpace.errors.uploadFailed', { name: file.name }));
                }
            } catch (err) {
                console.error('Upload error:', err);
                setError({ 
                    message: t('cloud:cloudSpace.errors.uploadFailed', { name: file.name }) 
                });
                setUploadProgress(null);
            }
        }
    };

    // Folder creation
    const handleCreateFolder = async (folderName, forOrg = false) => {
        setError(null);
        setActionLoading(true);
        
        const parentId = currentFolder?.id || null;
        const effectiveForOrg = currentScope === 'org' ? true : forOrg;
        
        if (effectiveForOrg && !(userInfo?.orgCode && userInfo?.level >= 1)) {
            setError({ message: t('cloud:cloudSpace.errors.noOrgPermission') });
            setActionLoading(false);
            return;
        }
        
        try {
            const response = await apiService.createFolder(folderName, parentId, effectiveForOrg);
            if (response.success) {
                setShowNewFolderModal(false);
                loadItems(parentId, currentScope);
            } else {
                setError({ message: response.message || t('cloud:cloudSpace.errors.folderCreateFailed') });
            }
        } catch (err) {
            setError({ 
                message: t('cloud:cloudSpace.errors.serverCreatingFolder') 
            });
        } finally {
            setActionLoading(false);
        }
    };

    // Vectorization
    const openVectorizeModal = (item) => {
        if (!item || item.item_type !== 'file') {
            setError({ message: t('cloud:cloudSpace.errors.invalidFileTypeName', { name: item?.name || t('cloud:cloudSpace.labels.selectFile') }) });
            return;
        }

        const supportedTypes = ['csv', 'xlsx', 'excel'];
        const fileName = item.name?.toLowerCase() || '';
        const isSupported = supportedTypes.includes(item.file_type) ||
                           fileName.endsWith('.csv') || fileName.endsWith('.xlsx') || fileName.endsWith('.xls');

        if (!isSupported) {
            setError({ message: t('cloud:cloudSpace.errors.onlyCsvExcelName', { name: item.name || t('cloud:cloudSpace.labels.selectFile') }) });
            return;
        }

        if (item.vectorized) {
            setItemToManage(item);
            setShowCombinationManager(true);
        } else {
            setItemToVectorize(item);
            setShowVectorizeModal(true);
        }
    };

    // Auto-update vectorization for all combinations (no modal, direct update)
    const handleAutoUpdateVectorization = async (item) => {
        if (!item || !item.vectorized) {
            setError({ message: t('cloud:cloudSpace.errors.fileNotVectorized') || 'File is not vectorized' });
            return;
        }

        setError(null);

        try {
            // Call the update API without combination_id to update ALL combinations
            const response = await cloudAPI.startIncrementalVectorization(item.id);

            if (response?.success && response?.task_id) {
                // Start tracking the first task (or primary task)
                setVectorizationTasks(prev => ({
                    ...prev,
                    [item.id]: {
                        taskId: response.task_id,
                        status: 'PENDING',
                        progress: 0,
                        description: t('cloud:cloudSpace.messages.updatingVectorization') || 'Updating vectorization...',
                        error: null,
                        totalCombinations: response.total_combinations || 1
                    }
                }));

                startProgressPolling(item.id, response.task_id);
            } else {
                throw new Error(response?.message || t('cloud:cloudSpace.errors.vectorizationUpdateFailed') || 'Failed to start vectorization update');
            }
        } catch (err) {
            console.error('Vectorization update error:', err);
            setError({
                message: err.response?.data?.error || err.message || t('cloud:cloudSpace.errors.vectorizationUpdateFailed') || 'Failed to update vectorization'
            });
        }
    };

    const handleVectorizeConfirm = async (fileId, sourceColumn, targetColumns) => {
        setError(null);
        setShowVectorizeModal(false);

        try {
            const response = await apiService.vectorizeFile(fileId, sourceColumn, targetColumns);
            
            if (response.success && response.task_id) {
                setVectorizationTasks(prev => ({
                    ...prev,
                    [fileId]: {
                        taskId: response.task_id,
                        status: 'PENDING',
                        progress: 0,
                        description: t('cloud:cloudSpace.messages.vectorizationStarting'),
                        error: null
                    }
                }));
                
                startProgressPolling(fileId, response.task_id);
            } else {
                throw new Error(response.message || t('cloud:cloudSpace.errors.vectorizationStartFailed'));
            }
        } catch (err) {
            console.error('Vectorization error:', err);
            setError({ 
                message: t('cloud:cloudSpace.errors.vectorizationFailed')
            });
        }
    };

    // Progress polling
    const startProgressPolling = (itemId, taskId) => {
        if (intervalRefs.current[taskId]) {
            clearInterval(intervalRefs.current[taskId]);
        }
        
        intervalRefs.current[taskId] = setInterval(async () => {
            try {
                const progressData = await apiService.getVectorizationProgress(taskId);
                
                if (progressData.success) {
                    const { state, result, current, total, meta } = progressData.data;
                    
                    const progress = total > 0 ? Math.round((current / total) * 100) : 0;
                    const description = meta?.description || t('cloud:cloudSpace.labels.processing');
                    
                    setVectorizationTasks(prev => ({
                        ...prev,
                        [itemId]: {
                            ...prev[itemId],
                            status: state === 'SUCCESS' ? 'SUCCESS' : state === 'FAILURE' ? 'FAILURE' : 'PROGRESS',
                            progress: state === 'SUCCESS' ? 100 : progress,
                            description: state === 'FAILURE' ? `${t('cloud:combination.error')}: ${result}` : description,
                            error: state === 'FAILURE' ? result : null
                        }
                    }));
                    
                    if (state === 'SUCCESS' || state === 'FAILURE') {
                        clearInterval(intervalRefs.current[taskId]);
                        delete intervalRefs.current[taskId];
                        
                        if (state === 'SUCCESS') {
                            // Remove the per-item task entry so UI reflects vectorized badge
                            setVectorizationTasks(prev => {
                                const updated = { ...prev };
                                if (updated[itemId]) delete updated[itemId];
                                return updated;
                            });
                            loadItems(currentFolder?.id, currentScope);
                        }
                    }
                } else {
                    // Handle unknown task status
                    setVectorizationTasks(prev => ({
                        ...prev,
                        [itemId]: {
                            ...prev[itemId],
                            status: 'FAILURE',
                            description: t('cloud:cloudSpace.messages.taskUnknown'),
                            error: t('cloud:cloudSpace.messages.taskStatusRetrieveFailed')
                        }
                    }));
                    clearInterval(intervalRefs.current[taskId]);
                    delete intervalRefs.current[taskId];
                }
            } catch (error) {
                console.error(`Error polling task ${taskId}:`, error);
                setVectorizationTasks(prev => ({
                    ...prev,
                    [itemId]: {
                        ...prev[itemId],
                        status: 'FAILURE',
                        description: t('cloud:cloudSpace.messages.connectionError'),
                        error: t('cloud:cloudSpace.messages.connectFailed')
                    }
                }));
                clearInterval(intervalRefs.current[taskId]);
                delete intervalRefs.current[taskId];
            }
        }, 3000);
    };

    // Other handlers
    const openRenameModal = (item) => {
        setItemToRename(item);
        setShowRenameModal(true);
    };

    const openDeleteModal = (item) => {
        setItemToDelete(item);
        setShowDeleteModal(true);
    };

    const handleRenameConfirm = async (newName) => {
        setActionLoading(true);
        try {
            const response = await apiService.renameItem(itemToRename.id, newName);
            if (response.success) {
                setShowRenameModal(false);
                setItemToRename(null);
                loadItems(currentFolder?.id, currentScope);
            } else {
                setError({ message: response.message || t('cloud:cloudSpace.errors.renameFailed') });
            }
        } catch (err) {
            setError({ 
                message: t('cloud:cloudSpace.errors.serverRenaming') 
            });
        } finally {
            setActionLoading(false);
        }
    };

    const handleDeleteConfirm = async () => {
        setActionLoading(true);
        try {
            const response = await apiService.deleteItem(itemToDelete.id);
            if (response.success) {
                setShowDeleteModal(false);
                setItemToDelete(null);
                if (selectedItemId === itemToDelete.id) {
                    setSelectedItemId(null);
                }
                loadItems(currentFolder?.id, currentScope);
            } else {
                setError({ message: response.message || t('cloud:cloudSpace.errors.deleteFailed') });
            }
        } catch (err) {
            setError({ 
                message: t('cloud:cloudSpace.errors.serverDeleting') 
            });
        } finally {
            setActionLoading(false);
        }
    };

    const handleDownload = async (item) => {
        setActionLoading(true);
        try {
            await apiService.downloadFile(item.id, item.name);
        } catch (err) {
            setError({ 
                message: t('cloud:cloudSpace.errors.downloadFailed') 
            });
        } finally {
            setActionLoading(false);
        }
    };

    // Filtered items
    const filteredItems = items.filter(item => 
        item.name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Render methods
    const renderBreadcrumbs = () => (
        <Breadcrumb listProps={{ style: { marginBottom: 0 } }}>
            {breadcrumbs.map((crumb, index) => (
                <Breadcrumb.Item
                    key={crumb.id || crumb.scope || 'root'}
                    onClick={() => handleBreadcrumbClick(crumb)}
                    active={index === breadcrumbs.length - 1}
                    href={index === breadcrumbs.length - 1 ? '#' : undefined}
                >
                    {crumb.name || (crumb.scope === 'org' ? t('cloud:cloudSpace.labels.orgDrive') : t('cloud:cloudSpace.labels.myDrive'))} 
                </Breadcrumb.Item>
            ))}
        </Breadcrumb>
    );

    const renderItemList = () => (
        <div className="table-responsive w-100">
            <Table hover size="sm" className="w-100">
                <thead>
                    <tr>
                        <th>{t('cloud:cloudSpace.labels.table.name')}</th>
                        <th>{t('cloud:cloudSpace.labels.table.type')}</th>
                        <th>{t('cloud:cloudSpace.labels.table.size')}</th>
                        <th>{t('cloud:cloudSpace.labels.table.status')}</th>
                        <th>{t('cloud:cloudSpace.labels.table.created')}</th>
                        <th>{t('cloud:cloudSpace.labels.table.actions')}</th>
                    </tr>
                </thead>
                <tbody>
                    {filteredItems.map(item => {
                        const modifiable = canModify(item);
                        const isVectorized = item.item_type === 'file' && item.vectorized;
                        const isVectorizationOutdated = item.item_type === 'file' && item.vectorized &&
                            (item.vectorization_stale || item.vectorization_outdated);
                        const task = vectorizationTasks[item.id];
                        const isProcessing = task && (task.status === 'PENDING' || task.status === 'PROGRESS');
                        const hasFailed = task && task.status === 'FAILURE';
                        
                        // For termBase/memoryBase mode, style non-vectorized files differently
                        const isSelectable = item.item_type === 'folder' || 
                            ((mode !== 'termBase' && mode !== 'memoryBase') || isVectorized);
                        
                        return (
                            <ItemRow
                                key={item.id}
                                onClick={() => isSelectable && handleItemClick(item)}
                                onDoubleClick={() => isSelectable && handleItemDoubleClick(item)}
                                className={`${selectedItemId === item.id ? 'selected' : ''} ${!isSelectable ? 'text-muted' : ''}`}
                                style={!isSelectable ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
                            >
                                <td>
                                    <ItemIcon>
                                        {item.item_type === 'folder'
                                            ? <FaFolder color={item.org_code ? '#fd7e14' : '#495057'} />
                                            : <FaFileAlt />}
                                    </ItemIcon>
                                    <FileName>{item.name}</FileName>
                                    {item.org_code && <Badge bg="warning" text="dark" className="ms-2">Org</Badge>}
                                    {isVectorized && !isProcessing && !hasFailed && (
                                        isVectorizationOutdated ? (
                                            <Badge pill bg="warning" text="dark" className="ms-1" title={t('cloud:cloudSpace.status.vectorizationOutdated') || 'Vectorization Outdated'}>V!</Badge>
                                        ) : (
                                            <Badge pill bg="info" className="ms-1" title={t('cloud:cloudSpace.labels.vectorized')}>V</Badge>
                                        )
                                    )} 
                                </td>
                                <td>{item.item_type === 'folder' ? t('cloud:cloudSpace.labels.table.folder') : (item.file_type || t('cloud:cloudSpace.labels.table.file'))}</td>
                                <td>{item.item_type === 'file' ? formatBytes(item.size_bytes) : '-'}</td>
                                <td>
                                    {item.item_type === 'folder' ? (
                                        '-'
                                    ) : isProcessing ? (
                                        <div style={{ minWidth: '150px' }}>
                                            <ProgressBar 
                                                now={task.progress} 
                                                label={`${task.progress}%`} 
                                                variant="info" 
                                                striped 
                                                animated 
                                                size="sm" 
                                            />
                                            <small className="text-muted d-block mt-1">{task.description || t('cloud:cloudSpace.labels.processing')}</small>
                                        </div>
                                    ) : hasFailed ? (
                                        <>
                                            <Badge bg="danger">{t('cloud:cloudSpace.labels.failed')}</Badge>
                                            {task.error && <small title={typeof task.error === 'string' ? task.error : JSON.stringify(task.error)} className="text-danger d-block mt-1">{t('cloud:combination.error')}: {typeof task.error === 'string' ? task.error : (task.error?.message || '未知错误')}</small>} 
                                        </>
                                    ) : isVectorized ? (
                                        isVectorizationOutdated ? (
                                            <Badge bg="warning" text="dark" title={t('cloud:cloudSpace.status.needsVectorizationUpdate') || 'Content changed - vectorization needs update'}>
                                                {t('cloud:cloudSpace.status.vectorizationOutdated') || 'Outdated'}
                                            </Badge>
                                        ) : (
                                            <Badge bg="success">{t('cloud:cloudSpace.labels.vectorized')}</Badge>
                                        )
                                    ) : (
                                        <Badge bg="secondary">{t('cloud:cloudSpace.labels.notVectorized')}</Badge>
                                    )}
                                </td>
                                <td>{formatDate(item.created_at)}</td>
                                <td>
                                    <Dropdown align="end">
                                        <Dropdown.Toggle variant="link" size="sm" bsPrefix="p-0">
                                            <FaEllipsisV />
                                        </Dropdown.Toggle>
                                        <Dropdown.Menu>
                                            {item.item_type === 'file' && (
                                                <Dropdown.Item onClick={() => handleDownload(item)}>
                                                    <FaDownload /> {t('cloud:cloudSpace.labels.download')}
                                                </Dropdown.Item>
                                            )}
                                            {/* Edit button for spreadsheet files */}
                                            {item.item_type === 'file' && (item.file_type === 'csv' || item.file_type === 'xlsx' || item.file_type === 'excel' ||
                                                item.name?.toLowerCase().endsWith('.csv') || item.name?.toLowerCase().endsWith('.xlsx') || item.name?.toLowerCase().endsWith('.xls')) && (
                                                <Dropdown.Item onClick={() => {
                                                    setEditingSpreadsheetItem(item);
                                                    setShowSpreadsheetEditor(true);
                                                }}>
                                                    <FaEdit /> {t('cloud:cloudSpace.actions.edit') || 'Edit'}
                                                </Dropdown.Item>
                                            )}
                                            {/* Update Vectorization button - shows when vectorization is outdated */}
                                            {item.item_type === 'file' && isVectorizationOutdated && modifiable && (
                                                <Dropdown.Item
                                                    onClick={() => handleAutoUpdateVectorization(item)}
                                                    disabled={isProcessing}
                                                    className="text-warning"
                                                >
                                                    <FaSyncAlt /> {t('cloud:cloudSpace.actions.updateVectorization') || 'Update Vectorization'}
                                                </Dropdown.Item>
                                            )}
                                            {item.item_type === 'file' && (item.file_type === 'csv' || item.file_type === 'xlsx' || item.file_type === 'excel' ||
                                                item.name?.toLowerCase().endsWith('.csv') || item.name?.toLowerCase().endsWith('.xlsx') || item.name?.toLowerCase().endsWith('.xls')) && modifiable && (
                                                <Dropdown.Item
                                                    onClick={() => openVectorizeModal(item)}
                                                    disabled={isProcessing}
                                                >
                                                    {isProcessing ? (
                                                        <><Spinner size="sm"/> {t('cloud:cloudSpace.labels.processing')}</>
                                                    ) : (
                                                        <><FaCog /> {isVectorized ? t('cloud:cloudSpace.labels.manageCombinations') : t('cloud:cloudSpace.labels.vectorize')}</>
                                                    )}
                                                </Dropdown.Item>
                                            )}
                                            {item.item_type === 'file' && (item.file_type === 'csv' || item.file_type === 'xlsx' || item.file_type === 'excel' ||
                                                item.name?.toLowerCase().endsWith('.csv') || item.name?.toLowerCase().endsWith('.xlsx') || item.name?.toLowerCase().endsWith('.xls')) && !modifiable && (
                                                <Dropdown.Item disabled>
                                                    <FaCog /> {isVectorized ? t('cloud:cloudSpace.labels.manageCombinations') : t('cloud:cloudSpace.labels.vectorize')} {t('cloud:cloudSpace.labels.noPermission')}
                                                </Dropdown.Item>
                                            )}
                                            {modifiable && (
                                                <Dropdown.Item onClick={() => openRenameModal(item)} disabled={isProcessing}>
                                                    <FaEdit /> {t('cloud:cloudSpace.labels.rename')}
                                                </Dropdown.Item>
                                            )}
                                            {!modifiable && (
                                                 <Dropdown.Item disabled><FaEdit /> {t('cloud:cloudSpace.labels.rename')} {t('cloud:cloudSpace.labels.noPermission')}</Dropdown.Item>
                                            )}
                                            {modifiable && (
                                                <Dropdown.Item className="text-danger" onClick={() => openDeleteModal(item)} disabled={isProcessing}>
                                                    <FaTrash /> {t('common:actions.delete')}
                                                </Dropdown.Item>
                                            )}
                                            {!modifiable && (
                                                <Dropdown.Item disabled className="text-danger">
                                                    <FaTrash /> {t('cloud:cloudSpace.labels.deleteNoPermission')}
                                                </Dropdown.Item>
                                            )}
                                        </Dropdown.Menu>
                                    </Dropdown>
                                </td>
                            </ItemRow>
                        );
                    })}
                </tbody>
            </Table>
        </div>
    );

    return (
        <>
            <Modal show={show} onHide={onHide} size="xl" centered>
                <Modal.Header closeButton>
                    <Modal.Title>
                        {t('cloud:cloudSpace.title')}
                        {(mode === 'termBase' || mode === 'memoryBase') && (
                            <Badge bg="info" className="ms-2">{t('cloud:cloudSpace.labels.selectVectorizedFile')}</Badge>
                        )}
                    </Modal.Title>
                </Modal.Header>
                <CloudSpaceContainer style={{position: 'relative'}}>
                    {/* Upload Progress Overlay - Now inside CloudSpaceContainer */}
                {uploadProgress && (
                    <UploadProgress>
                        <h6>{t('cloud:cloudSpace.labels.uploading', { name: uploadProgress.fileName })}</h6>
                        <ProgressBar 
                            now={uploadProgress.progress} 
                            label={`${uploadProgress.progress}%`}
                            className="mb-2"
                        />
                        {uploadProgress.completed && (
                            <small className="text-success">{t('cloud:cloudSpace.labels.uploadCompleted')}</small>
                        )}
                    </UploadProgress>
                )}
                    
                    <HeaderSection>
                        {renderBreadcrumbs()}
                        <ActionButtons>
                            <InputGroup size="sm" style={{ width: '200px' }}>
                                <FormControl
                                    placeholder={t('cloud:cloudSpace.labels.searchPlaceholder')}
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                                <Button variant="outline-secondary" disabled><FaSearch /></Button> 
                            </InputGroup>
                            <Button 
                                variant="outline-secondary" 
                                size="sm" 
                                onClick={() => {
                                    console.log('Refresh button clicked:', {
                                        currentFolder: currentFolder,
                                        currentScope: currentScope
                                    });
                                    loadItems(currentFolder?.id, currentScope);
                                }} 
                                disabled={loading}
                            >
                                <FaSyncAlt />
                            </Button>
                            <Dropdown>
                                <Dropdown.Toggle variant="primary" size="sm" disabled={actionLoading}>
                                    {t('common:actions.new')}
                                </Dropdown.Toggle>
                                <Dropdown.Menu>
                                    <Dropdown.Item onClick={() => setShowNewFolderModal(true)} disabled={actionLoading}>
                                        <FaFolderPlus /> {t('cloud:cloudSpace.labels.newFolder')}
                                    </Dropdown.Item>
                                    <Dropdown.Item onClick={() => fileInputRef.current?.click()} disabled={actionLoading}>
                                        <FaCloudUploadAlt /> {t('cloud:cloudSpace.labels.uploadFile')}
                                    </Dropdown.Item>
                                </Dropdown.Menu>
                            </Dropdown>
                            <input
                                type="file"
                                ref={fileInputRef}
                                style={{ display: 'none' }}
                                onChange={handleFileUpload}
                                accept=".csv,.xlsx,.xls"
                                multiple
                            />
                        </ActionButtons>
                    </HeaderSection>

                    <MainContent>
                        <Sidebar>
                             <ListGroup variant="flush">
                                 <ListGroup.Item action active={currentScope === 'user'} onClick={() => loadItems(null, 'user')}>
                                     <FaUserCircle className="me-2" /> {t('cloud:cloudSpace.labels.myDrive')}
                                 </ListGroup.Item>
                                 {userInfo?.orgCode && (
                                     <ListGroup.Item action active={currentScope === 'org'} onClick={() => loadItems(null, 'org')}>
                                         <FaBuilding className="me-2" /> {t('cloud:cloudSpace.labels.orgDrive')}
                                     </ListGroup.Item>
                                 )}
                             </ListGroup>
                        </Sidebar>
                        <FileBrowser>
                            {(mode === 'termBase' || mode === 'memoryBase') && (
                            <Alert variant="info" className="mb-2">
                                {t('cloud:cloudSpace.labels.noteVectorizedOnly', { mode: (mode === 'termBase' ? t('workflow:modules.termBase.name') : t('workflow:modules.memoryBase.name')) })}
                            </Alert>
                            )}
                            {error && (
                                <Alert variant="danger" onClose={() => setError(null)} dismissible>
                                    {typeof error === 'string' ? error : (error?.message || JSON.stringify(error) || '未知错误')}
                                </Alert>
                            )}
                            {loading ? (
                                <div className="text-center p-5">
                                    <Spinner animation="border" /> {t('common:status.loading')}
                                </div>
                            ) : filteredItems.length === 0 ? (
                                <div className="text-center p-5 text-muted">
                                    {searchTerm ? t('cloud:cloudSpace.labels.noMatches') : t('cloud:cloudSpace.labels.emptyFolder')}
                                </div>
                            ) : (
                                <ItemList>
                                    {renderItemList()} 
                                </ItemList>
                            )}
                        </FileBrowser>
                    </MainContent>
                </CloudSpaceContainer>

                <FooterSection>
                    <Button variant="secondary" onClick={onHide}>{t('cloud:cloudSpace.labels.close')}</Button>
                    {mode && (
                    <Button
                        variant="primary"
                        onClick={() => {
                            const selectedItem = items.find(item => item.id === selectedItemId);
                            
                            // For termBase and memoryBase, enforce vectorization requirement
                            if (mode === 'termBase' || mode === 'memoryBase') {
                                if (!selectedItem) {
                                    alert(t('cloud:cloudSpace.alerts.selectFileFirst'));
                                    return;
                                }
                                if (selectedItem.item_type !== 'file') {
                                    alert(t('cloud:cloudSpace.alerts.selectFileNotFolder'));
                                    return;
                                }
                                if (!selectedItem.vectorized) {
                                    alert(t('cloud:cloudSpace.alerts.selectVectorizedFirst'));
                                    return;
                                }
                                if (!selectedItem.vectorization_combinations) {
                                    alert(t('cloud:cloudSpace.alerts.noCombinations'));
                                    return;
                                }
                            }
                            
                            if (selectedItem && selectedItem.item_type === 'file') {
                                // For term/memory base, require vectorization
                                if ((mode === 'termBase' || mode === 'memoryBase') && !selectedItem.vectorized) {
                                    return;
                                }
                                onSelectFile(selectedItem); 
                                onHide();
                            }
                        }}
                        disabled={
                            !selectedItemId || 
                            items.find(item => item.id === selectedItemId)?.item_type !== 'file' ||
                            ((mode === 'termBase' || mode === 'memoryBase') && !items.find(item => item.id === selectedItemId)?.vectorized)
                        }
                        title={
                             !selectedItemId ? t('common:tooltips.selectFileFirst', { defaultValue: 'Select a file from the list first' }) :
                             items.find(item => item.id === selectedItemId)?.item_type !== 'file' ? t('common:tooltips.selectionMustBeFile', { defaultValue: 'Selection must be a file' }) :
                             ((mode === 'termBase' || mode === 'memoryBase') && !items.find(item => item.id === selectedItemId)?.vectorized) ? t('cloud:cloudSpace.labels.selectedMustBeVectorized') :
                             t('common:tooltips.selectThisFile', { defaultValue: 'Select this file' })
                         }
                    >
                        {(mode === 'termBase' || mode === 'memoryBase') ? t('cloud:cloudSpace.labels.selectVectorizedFile') : t('cloud:cloudSpace.labels.selectFile')}
                    </Button>
                    )}
                </FooterSection>


                {/* Modals */}
                <Modal show={showNewFolderModal} onHide={() => setShowNewFolderModal(false)} centered size="sm">
                     <Modal.Header closeButton>
                         <Modal.Title>{t('cloud:cloudSpace.labels.newFolder')}</Modal.Title>
                     </Modal.Header>
                     <Modal.Body>
                         <Form onSubmit={(e) => {
                             e.preventDefault();
                             const folderName = e.target.elements.folderName.value;
                             const forOrg = currentScope === 'org'; 
                             handleCreateFolder(folderName, forOrg);
                             e.target.elements.folderName.value = '';
                         }}>
                             <Form.Group>
                                 <Form.Label>{t('cloud:cloudSpace.labels.folderNameIn', { drive: (currentScope === 'org' ? `${t('cloud:cloudSpace.labels.orgDrive')}: ${userInfo?.orgCode}` : t('cloud:cloudSpace.labels.myDrive')) })}</Form.Label>
                                 <Form.Control type="text" name="folderName" required autoFocus />
                             </Form.Group>
                             <Button variant="primary" type="submit" className="mt-3 w-100" disabled={actionLoading || (currentScope === 'org' && !(userInfo?.orgCode && userInfo?.level >= 1))}>
                                  {actionLoading ? <><Spinner size="sm"/> {t('cloud:cloudSpace.labels.creating')}</> : t('cloud:cloudSpace.labels.create')}
                              </Button>
                         </Form>
                     </Modal.Body>
                </Modal>

                <VectorizeConfigModal
                    show={showVectorizeModal}
                    onHide={() => setShowVectorizeModal(false)}
                    file={itemToVectorize}
                    onConfirm={handleVectorizeConfirm}
                    loading={false}
                />
                
                <VectorizationCombinationManager
                    show={showCombinationManager}
                    onHide={() => setShowCombinationManager(false)}
                    file={itemToManage}
                    onUpdate={(eventData) => {
                        if (eventData && eventData.type === 'new_combination_started') {
                            // Start tracking the new vectorization task
                            const { taskId, fileId } = eventData;
                            if (taskId && fileId) {
                                setVectorizationTasks(prev => ({
                                    ...prev,
                                    [fileId]: {
                                        taskId: taskId,
                                        status: 'PENDING',
                                        progress: 0,
                                        description: 'Adding new vectorization combination...',
                                        error: null
                                    }
                                }));
                                
                                // Start progress polling for the new task
                                startProgressPolling(fileId, taskId);
                            }
                        }
                        
                        // Always refresh the items list
                        loadItems(currentFolder?.id, currentScope);
                    }}
                />
                
                <RenameModal
                    show={showRenameModal}
                    onHide={() => setShowRenameModal(false)}
                    currentName={itemToRename?.name}
                    onConfirm={handleRenameConfirm}
                    loading={actionLoading}
                />
                
                <DeleteConfirmationModal
                    show={showDeleteModal}
                    onHide={() => setShowDeleteModal(false)}
                    itemName={itemToDelete?.name}
                    onConfirm={handleDeleteConfirm}
                    loading={actionLoading}
                />

                {/* Spreadsheet Editor Modal */}
                <SpreadsheetEditorModal
                    show={showSpreadsheetEditor}
                    onHide={() => {
                        setShowSpreadsheetEditor(false);
                        setEditingSpreadsheetItem(null);
                    }}
                    fileItem={editingSpreadsheetItem}
                    onSaveSuccess={() => {
                        // Refresh the items list after successful save
                        loadItems(currentFolder?.id, currentScope);
                    }}
                    readOnly={editingSpreadsheetItem ? !canModify(editingSpreadsheetItem) : true}
                />
            </Modal>
        </>
    );
};

export default CloudSpaceNew;
